import type { AddonSuggestionType } from '@/types/addonSuggestions';

/**
 * CORE-BP item 5 — what the shop is allowed to be told about its own rules.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * THE QUESTION THIS ANSWERS, AND THE ONE IT MUST NOT
 * ═══════════════════════════════════════════════════════════════════════════
 * It answers: "is this rule helping us ask the right technical question?" A
 * rule that fires constantly and is refused constantly is, in CORE-BP's own
 * words, a defect in the rule — and until now a shop had no way to see that,
 * because the aggregation had never run and nothing rendered it.
 *
 * It must not answer "which advisor sells these best". That is not prevented
 * here by choosing not to render a column: item 9 removed the person dimension
 * from the events themselves and closed raw reads, so there is no advisor in
 * the data this reads. This module could not show one if it wanted to.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * WHY THE STATES ARE A DISCRIMINATED UNION
 * ═══════════════════════════════════════════════════════════════════════════
 * "No outcome data yet" and "0% acceptance" are different claims, and the
 * second one is a lie when the aggregation simply has not run — which, given
 * item 4 has only just been scheduled, is the state every existing shop is in
 * today. A failed READ is a third thing again: it is not zero and not empty,
 * it is unknown.
 *
 * Collapsing those three into "render the numbers you have" is how a capability
 * ends up telling a shop its rules are being refused when in fact nobody has
 * measured them. So the caller cannot get a number out of this module without
 * first having been told which of the three situations it is in.
 */

/** Exactly the person-free columns the aggregate exposes. */
export interface AddonAnalyticsReadRow {
  suggestion_type: string;
  period_start: string;
  period_end: string;
  total_shown: number | null;
  total_accepted: number | null;
  total_dismissed: number | null;
  acceptance_rate: number | null;
  dismissal_rate: number | null;
}

export interface AddonRuleQualityFamily {
  suggestionType: string;
  /** The same words the advisor saw on the suggestion itself. */
  label: string;
  shown: number;
  accepted: number;
  declined: number;
  acceptanceRate: number;
  declineRate: number;
}

export type AddonRuleQualityView =
  /** The read failed. NOT zero, and NOT empty — unknown. */
  | { state: 'unavailable' }
  /** The read succeeded and there is nothing measured yet. */
  | { state: 'empty' }
  | {
      state: 'ready';
      periodStart: string;
      periodEnd: string;
      families: AddonRuleQualityFamily[];
    };

/**
 * Vehicle and work language, taken from the suggestion the advisor actually
 * saw. Deliberately not sales language: nothing here is a sale, a quote, a
 * conversion or a close.
 */
const FAMILY_LABELS: Record<AddonSuggestionType, string> = {
  alignment: 'Four-wheel alignment',
  sas: 'Steering Angle Sensor (SAS) reset',
  adas: 'ADAS calibration',
  tpms: 'TPMS relearn',
};

const labelFor = (type: string): string =>
  FAMILY_LABELS[type as AddonSuggestionType] ?? type;

/** Percentage to two places; falls back to the counts if the stored rate is null. */
const rateOf = (stored: number | null, part: number, whole: number): number => {
  if (stored !== null && stored !== undefined && Number.isFinite(stored)) return stored;
  return whole > 0 ? Math.round((part / whole) * 10000) / 100 : 0;
};

/**
 * Reduce the aggregate rows to the most recent settled period.
 *
 * Only one period is shown. Summing across periods in the browser would
 * double-count the overlapping windows an operator-run replay can produce, and
 * "the period represented" is one of the things item 5 requires the surface to
 * state honestly rather than imply.
 *
 * `failed` is separate from the rows on purpose: a read that errors must not be
 * able to arrive here as an empty array and be reported as "no data yet".
 */
export function deriveAddonRuleQuality(
  rows: readonly AddonAnalyticsReadRow[] | null | undefined,
  failed = false,
): AddonRuleQualityView {
  if (failed) return { state: 'unavailable' };
  if (!rows || rows.length === 0) return { state: 'empty' };

  // The latest period_start present. Rows are not trusted to arrive ordered.
  const latest = rows.reduce(
    (max, r) => (r.period_start > max ? r.period_start : max), rows[0].period_start);

  const inPeriod = rows.filter((r) => r.period_start === latest);
  if (inPeriod.length === 0) return { state: 'empty' };

  const families = inPeriod
    .map((r) => {
      const shown = r.total_shown ?? 0;
      const accepted = r.total_accepted ?? 0;
      const declined = r.total_dismissed ?? 0;
      return {
        suggestionType: r.suggestion_type,
        label: labelFor(r.suggestion_type),
        shown,
        accepted,
        declined,
        acceptanceRate: rateOf(r.acceptance_rate, accepted, shown),
        declineRate: rateOf(r.dismissal_rate, declined, shown),
      };
    })
    // Most-shown first: the rule the shop asks about most is the one whose
    // quality matters most.
    .sort((a, b) => (b.shown - a.shown) || a.label.localeCompare(b.label));

  return {
    state: 'ready',
    periodStart: latest,
    periodEnd: inPeriod.reduce((max, r) => (r.period_end > max ? r.period_end : max),
      inPeriod[0].period_end),
    families,
  };
}

/** `2026-05-01` → `1 May 2026`. Dates are stated, never implied as "live". */
export function formatPeriodDay(day: string): string {
  const parsed = new Date(`${day}T00:00:00.000Z`);
  if (Number.isNaN(parsed.getTime())) return day;
  return parsed.toLocaleDateString('en-GB', {
    day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC',
  });
}

/** "1 May 2026" for a single day, "1–7 May 2026" style range otherwise. */
export function formatPeriodRange(start: string, end: string): string {
  return start === end
    ? formatPeriodDay(start)
    : `${formatPeriodDay(start)} – ${formatPeriodDay(end)}`;
}
